/**
 * 路由表 + 🔴 **未登录一律回登录页**（唯一的路由守卫）
 *
 * ## 为什么守卫放在端侧而不是"让后端拦"
 * 后端**当然**会拦（每个接口都过 `AuthGuard` + `LedgerGuard`）—— 端侧守卫只解决**体验**：
 * 不给未登录用户看到空壳页面与一串 401。🔴 **它不构成安全边界**，别把它当权限。
 * （架构 §6.3：权限判定的唯一处是服务端守卫；端侧角色只用于**隐藏协作动作入口**。）
 */

import { Navigate, Route, Routes } from 'react-router-dom';
import { useApp } from '../app/store.js';
import { Shell } from './Shell.js';
import { LoginPage } from '../pages/LoginPage.js';
import { HomePage } from '../pages/HomePage.js';
import { JournalPage } from '../pages/JournalPage.js';
import { PlanPage } from '../pages/PlanPage.js';
import { AccountsPage } from '../pages/AccountsPage.js';
import { NewTransactionPage } from '../pages/NewTransactionPage.js';
import { PoliciesPage } from '../pages/PoliciesPage.js';
import { SettingsPage } from '../pages/SettingsPage.js';
import { ReportPage } from '../pages/ReportPage.js';
import { ReminderPage } from '../pages/ReminderPage.js';
import { GoalsPage } from '../pages/GoalsPage.js';
import { ImportPage } from '../pages/ImportPage.js';
import { PaydayPage } from '../pages/PaydayPage.js';
import { TermsPage } from '../pages/TermsPage.js';
import { PrivacyPage } from '../pages/PrivacyPage.js';
import { MajorAssetsPage } from '../pages/MajorAssetsPage.js';
import { MajorAssetDetailPage } from '../pages/MajorAssetDetailPage.js';
import { DebtsPage } from '../pages/DebtsPage.js';
import { ContraDetailPage } from '../pages/ContraDetailPage.js';
import { AssetsPage } from '../pages/AssetsPage.js';
import { InvestmentsPage } from '../pages/InvestmentsPage.js';
import { InstallmentsPage } from '../pages/InstallmentsPage.js';
import { CreditBillPage } from '../pages/CreditBillPage.js';

export function AppRoutes() {
  const { session } = useApp();

  // 会话是从 localStorage 同步恢复的，故这里**没有 loading 态**（不会闪登录页）
  if (session === null) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        {/*
          🔴 法律页**未登录也要能打开**（N5：注册前必须能读到协议与隐私政策）⇒
          两条路由必须在**未登录分支**里各写一次 —— 只写在登录分支里，
          未登录点链接会被 `<Route path="*">` 重定向回登录页。
        */}
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        {/* 任何其它路径（含 /）都回登录页 */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<Navigate to="/" replace />} />
      {/*
        🔴 法律页放在 `Shell` **之外**（与 `/login` 同级）—— 它们是独立文档，
        带侧栏/顶栏反而降低可读性；且必须与"是否登录"无关（同一份 URL 两种状态都能进）。
      */}
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route element={<Shell />}>
        <Route path="/" element={<HomePage />} />
        {/* 🆕 ㊼ 批：流水页（C 域 C1）—— 侧栏「日常 · 流水」的落点 */}
        <Route path="/journal" element={<JournalPage />} />
        {/* 🆕 F 域批：计划页（F2 未来必付日历）—— 侧栏「日常 · 计划」的落点 */}
        <Route path="/plan" element={<PlanPage />} />
        {/* 🆕 K 域批：报表页（四段全时间轴）—— 侧栏「家底 · 报表」的落点 */}
        <Route path="/report" element={<ReportPage />} />
        {/* 🆕 H 域批：提醒中心（H2 站内兜底通道 —— 不依赖任何授权） */}
        <Route path="/reminders" element={<ReminderPage />} />
        {/* 🆕 批 95：资产 = 汇总；账户 = 全量台账（原 /accounts 的列表搬到这里） */}
        <Route path="/assets" element={<AssetsPage />} />
        <Route path="/accounts" element={<AccountsPage />} />
        {/* 🆕 批 99 B2：投资页（S 域 · 持仓 / 买卖赎回 / 三档回报率 + 年化）—— `DR-059` D1 */}
        <Route path="/investments" element={<InvestmentsPage />} />
        {/*
          🆕 批 106-A：信用卡账单（`DR-061`）—— **不进侧栏**（`DR-025` 三组 10 项不变）。
          入口 = 卡面「账单」按钮（`CreditCardSection`）。
          ⚠️ `/accounts/:accountId/bill` 的 `:accountId` 段必须写在 `/accounts` 之后
          （字面量在前，参数在后 —— 铁律 90）。
        */}
        <Route path="/accounts/:accountId/bill" element={<CreditBillPage />} />
        {/*
          🆕 批 104：信用卡分期（`DR-060`）—— **不进侧栏**（三组 12 项不变）。
          入口 = 卡面「分期」按钮（`CreditCardSection`）。
          ⚠️ 字面量段在前、`:id` 段在后（铁律 90）。
        */}
        <Route path="/installments" element={<InstallmentsPage />} />
        <Route path="/installments/:id" element={<InstallmentsPage />} />
        <Route path="/debts" element={<DebtsPage />} />
        {/*
          🆕 批 111：借贷明细（`AC-Q4.3` 成页）—— **不进侧栏**（`DR-025` 三组 10 项不变）。
          入口 = 卡片「明细」抽屉里的「整页」按钮；本路由的价值是**可直达 / 可收藏**
          （`/debts/<contraId>` 是稳定的深链接，内容由 `GET /api/contra/:id/timeline` 下发）。
          ⚠️ 字面量段在前、`:contraId` 段在后（铁律 90）。
        */}
        <Route path="/debts/:contraId" element={<ContraDetailPage />} />
        {/*
          🆕 批 92：R 域（重大资产）—— **不进侧栏**（`DR-025` 三组 10 项不变）。
          入口 = 资产页的「重大资产」区块（`AC-R4.1`：直达 ≤2 跳 = 资产页 → 区块 → 详情）。
          ⚠️ `:id` 段必须写在 `/major-assets` 之后（字面量在前，参数在后 —— 铁律 90 的同一纪律）。
        */}
        <Route path="/major-assets" element={<MajorAssetsPage />} />
        <Route path="/major-assets/:id" element={<MajorAssetDetailPage />} />
        <Route path="/policies" element={<PoliciesPage />} />
        <Route path="/goals" element={<GoalsPage />} />
        <Route path="/import" element={<ImportPage />} />
        {/* 🆕 F3 发薪日：**不进导航**（`DR-025` 三组 10 项不变），从「储蓄目标」页的提示条进入 */}
        <Route path="/paydays" element={<PaydayPage />} />
        <Route path="/new" element={<NewTransactionPage />} />
        {/*
          🔴 设置**不在侧栏**，但它是**一条真实路由**（`AC-O1.6`：入口在顶栏、不占导航位）——
          顶栏齿轮是它的唯一入口。⚠️ 它**故意不在 `nav.tsx` 的 `NAV` 里**：
          放进去就会在侧栏长出一个导航位，与 `AC-O1.6` 直接冲突。
          ⇒ 代价是 `titleOf()` 认不出它（那里只遍历 `NAV`）⇒ 在下面显式补一条。
        */}
        <Route path="/settings" element={<SettingsPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
